import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  CheckCircle as AcceptIcon,
  Cancel as DeclineIcon,
  HelpOutline as TentativeIcon,
  VideoCall as VideoCallIcon,
  PersonAdd as PersonAddIcon,
} from "@mui/icons-material";
import { CalendarEvent } from "../types/calendar";
import { calendarService } from "../services/CalendarService";
import { DateUtils } from "../utils/dateUtils";

interface EventDetailsPopupProps {
  event: CalendarEvent;
  onEventUpdate: (event: CalendarEvent) => void;
  onEventDelete: (eventId: string) => void;
  onEditEvent: (event: CalendarEvent) => void;
  onClose: () => void;
}

export const EventDetailsPopup: React.FC<EventDetailsPopupProps> = ({ event, onEventUpdate, onEventDelete, onEditEvent, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleResponse = async (response: "accepted" | "declined" | "tentative") => {
    setLoading(true);
    setError("");

    try {
      const updatedEvent = await calendarService.respondToEvent(event.id, response);
      onEventUpdate(updatedEvent);
      // Don't close the popup immediately - let the user see the updated button state
      // onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to respond to event");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    onEditEvent(event);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${event.title}"?`)) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      await calendarService.deleteEvent(event.id);
      onEventDelete(event.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete event");
    } finally {
      setLoading(false);
    }
  };

  const isEditable = event.editable !== false; // Default to true if not specified
  const isExternal = event.external === true;

  // Check if user needs to respond (only needsAction should show as "not responded")
  const needsResponse = isExternal && event.userResponseStatus === "needsAction";

  // Get current user email for display
  const currentUserEmail = event.userEmail;

  const formatEventTime = (date: Date) => {
    if (!DateUtils.isValidDate(date)) return "Invalid time";
    return DateUtils.formatTime(date);
  };

  const formatEventDate = (date: Date) => {
    if (!DateUtils.isValidDate(date)) return "Invalid date";
    return DateUtils.formatForDisplay(date);
  };

  const formatAllDayEventDate = (date: Date) => {
    if (!DateUtils.isValidDate(date)) return "Invalid date";
    return DateUtils.formatForDisplay(date);
  };

  const formatAllDayEventRange = (startDate: Date, endDate: Date) => {
    if (!DateUtils.isValidDate(startDate) || !DateUtils.isValidDate(endDate)) return "Invalid date";

    // For all-day events, the end date is actually the start of the next day
    // So we need to subtract one day to get the actual end day
    const actualEndDate = new Date(endDate);
    actualEndDate.setDate(actualEndDate.getDate() - 1);

    const startFormatted = DateUtils.formatForDisplay(startDate);
    const endFormatted = DateUtils.formatForDisplay(actualEndDate);

    // If it's the same day, just show the day
    if (startFormatted === endFormatted) {
      return startFormatted;
    }

    // Otherwise show the range
    return `${startFormatted} - ${endFormatted}`;
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" component="div">
            {event.title}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Event Status Indicators */}
        <Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
          {isExternal && <Chip icon={<PersonAddIcon sx={{ fontSize: 16 }} />} label="External Event" size="small" color="info" variant="outlined" />}
          {needsResponse && <Chip label="Not Responded" size="small" color="warning" variant="outlined" />}
          {!isEditable && <Chip label="Read Only" size="small" color="default" variant="outlined" />}
        </Box>

        {/* Event Details */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <ScheduleIcon sx={{ fontSize: 16, mr: 1, color: "text.secondary" }} />
            <Typography variant="body2" color="text.secondary">
              {event.allDay ? formatAllDayEventRange(event.startTime, event.endTime) : `${formatEventDate(event.startTime)} • ${formatEventTime(event.startTime)} - ${formatEventTime(event.endTime)}`}
            </Typography>
          </Box>

          {event.location && (
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <LocationIcon sx={{ fontSize: 16, mr: 1, color: "text.secondary" }} />
              <Typography variant="body2" color="text.secondary">
                {event.location}
              </Typography>
            </Box>
          )}

          {event.meetingLink && (
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <VideoCallIcon sx={{ fontSize: 16, mr: 1, color: "text.secondary" }} />
              <Typography variant="body2" color="primary" component="a" href={event.meetingLink} target="_blank" rel="noopener noreferrer">
                Join Meeting
              </Typography>
            </Box>
          )}

          {event.description && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {event.description}
              </Typography>
            </Box>
          )}

          {/* Attendees */}
          {event.attendees && event.attendees.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, display: "flex", alignItems: "center" }}>
                <PeopleIcon sx={{ fontSize: 16, mr: 1 }} />
                Attendees ({event.attendees.length})
              </Typography>
              <List dense sx={{ py: 0 }}>
                {event.attendees.map((attendee, index) => (
                  <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                    <ListItemText primary={attendee.displayName || attendee.email} />
                    <ListItemIcon sx={{ minWidth: "auto" }}>
                      <Chip
                        label={
                          attendee.responseStatus === "needsAction"
                            ? "Not Responded"
                            : attendee.responseStatus === "accepted"
                              ? "Accepted"
                              : attendee.responseStatus === "declined"
                                ? "Declined"
                                : attendee.responseStatus === "tentative"
                                  ? "Maybe"
                                  : attendee.responseStatus
                        }
                        size="small"
                        color={attendee.responseStatus === "accepted" ? "success" : attendee.responseStatus === "declined" ? "error" : "warning"}
                        variant="outlined"
                      />
                    </ListItemIcon>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Organizer Info */}
          {event.organizerEmail && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Organizer:</strong> {event.organizerEmail}
              </Typography>
            </Box>
          )}

          {/* Current User Response Status */}
          {isExternal && currentUserEmail && event.userResponseStatus && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Your Response:</strong>{" "}
                {event.userResponseStatus === "needsAction"
                  ? "Not Responded"
                  : event.userResponseStatus === "accepted"
                    ? "Accepted"
                    : event.userResponseStatus === "declined"
                      ? "Declined"
                      : event.userResponseStatus === "tentative"
                        ? "Maybe"
                        : event.userResponseStatus}
              </Typography>
            </Box>
          )}

          {/* Classification */}
          {event.classification && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Classification:</strong> {event.classification}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Close
        </Button>

        {/* Response buttons for external events */}
        {isExternal && (
          <>
            <Button
              onClick={() => handleResponse("declined")}
              disabled={loading}
              startIcon={<DeclineIcon />}
              color="error"
              variant={event.userResponseStatus === "declined" ? "contained" : "outlined"}
            >
              Decline
            </Button>
            <Button
              onClick={() => handleResponse("tentative")}
              disabled={loading}
              startIcon={<TentativeIcon />}
              color="warning"
              variant={event.userResponseStatus === "tentative" ? "contained" : "outlined"}
            >
              Maybe
            </Button>
            <Button
              onClick={() => handleResponse("accepted")}
              disabled={loading}
              startIcon={<AcceptIcon />}
              color="success"
              variant={event.userResponseStatus === "accepted" ? "contained" : "outlined"}
            >
              Accept
            </Button>
          </>
        )}

        {/* Edit button for editable events (not external) */}
        {isEditable && !isExternal && (
          <Button onClick={handleEdit} disabled={loading} startIcon={<EditIcon />} variant="contained">
            Edit
          </Button>
        )}

        {/* Delete button - allow for all events */}
        <Button onClick={handleDelete} disabled={loading} color="error" variant="outlined">
          Delete
        </Button>

        {loading && <CircularProgress size={20} />}
      </DialogActions>
    </Dialog>
  );
};
